import { useT } from '@transifex/react';
import type {
	InputHTMLAttributes,
	ReactNode,
	ChangeEvent,
	WheelEvent,
	SyntheticEvent,
} from 'react';
import { useFormContext } from 'react-hook-form';

import { useDebounce } from 'pkg/timings';
import withForwardedRef from 'pkg/withForwardedRef';

import type { InputChangeEvent } from 'components/form/inputs';
import InputBase from 'components/form/inputs/input_base';

import * as css from './styles.css';

/**
 * Props interface for the Field component.
 * Extends standard input attributes and adds support for custom styling, debounced changes,
 * validation, and integration with react-hook-form.
 */
export interface Props extends Omit<
	InputHTMLAttributes<HTMLInputElement>,
	'onChange'
> {
	rounded?: boolean;
	square?: boolean;
	small?: boolean;
	filled?: boolean;
	transparent?: boolean;
	hidden?: boolean;
	value?: string | number;
	defaultValue?: string | number;
	disabled?: boolean;
	changeDelay?: number;
	readOnly?: boolean;
	className?: string;
	forwardedRef?: any;
	testid?: string;
	onChange?: InputChangeEvent;
	onTyping?: InputChangeEvent;
	// Callback for custom validation
	validate?: (
		value: string | number
	) => string | string[] | boolean | undefined;
	// This has the any type because typescript doesn't like forwardedRef.current
	// when we used Ref<HTMLlInputElement> type
	children?: ReactNode;
	// provide a function to transform the input data, see register() of react-hook-form
	setValueAs?: (value: any) => any;
}
/**
 * Field component
 *
 * A highly customizable input field that integrates with react-hook-form.
 */
export const Field = withForwardedRef(
	({
		rounded,
		square,
		small,
		filled,
		transparent,
		hidden,
		value,
		defaultValue,
		disabled,
		className,
		readOnly,
		changeDelay,
		forwardedRef,
		testid,
		onChange,
		onTyping,
		validate,
		children,
		setValueAs,

		...rest
	}: Props) => {
		const t = useT();
		const methods = useFormContext();

		const validation = readOnly
			? {}
			: {
					required: rest.required,
					minLength: rest.minLength,
					maxLength: rest.maxLength,
					min: rest.min,
					max: rest.max,
					pattern: new RegExp(rest.pattern),
					validate: (value: string | number) => {
						if (typeof value === 'string' && rest.required) {
							if (value.trim() === '') {
								return t('This field cannot be blank');
							}
						}

						if (validate) {
							return validate(value);
						}

						return true;
					},
				};

		const validationRules =
			rest.name &&
			methods &&
			methods.register(rest.name, {
				...validation,
				disabled,
				setValueAs,
			});

		const emitChange = useDebounce(
			(event: ChangeEvent<HTMLInputElement> | SyntheticEvent) => {
				if (validationRules) {
					validationRules.onChange(event);
				}
				onChange(event);
			},
			changeDelay
		);

		let changeHandler: InputChangeEvent;
		let inputHandler: InputChangeEvent;

		if (onChange && !changeDelay) {
			changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
				if (validationRules) {
					validationRules.onChange(event);
				}

				onChange(event);
			};
		} else if (onChange && changeDelay) {
			inputHandler = (event: SyntheticEvent) => {
				if (onTyping) {
					onTyping(event);
				}

				emitChange(event);
			};
		} else if (!onChange && validationRules) {
			changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
				validationRules.onChange(event);
			};
		}

		let ref = forwardedRef;

		if (forwardedRef && validationRules) {
			ref = (e: HTMLInputElement) => {
				validationRules.ref(e);
				forwardedRef.current = e; // you can still assign to ref
			};
		} else if (validationRules) {
			ref = validationRules.ref;
		}

		let onScrollHandler;
		if (rest.type === 'number') {
			onScrollHandler = (e: WheelEvent<HTMLInputElement>) => {
				if (document.activeElement === e.currentTarget) {
					e.currentTarget.blur();
				}
			};
		}
		if (rest.type === 'hidden') {
			hidden = true;
		}

		return (
			<InputBase
				rounded={rounded}
				square={square}
				small={small}
				filled={filled}
				transparent={transparent}
				hidden={hidden}
				data-disabled={disabled}
				className={className}>
				{children}
				<input
					{...rest}
					ref={ref}
					disabled={disabled}
					readOnly={readOnly}
					onChange={changeHandler}
					onInput={inputHandler}
					onWheel={onScrollHandler}
					defaultValue={defaultValue}
					value={value}
					className={css.field}
					data-testid={testid}
				/>
			</InputBase>
		);
	}
);
